import React, { useState, useEffect } from 'react';
import { questionnaireAPI } from '../../services/api';
import { Copy, Share2 } from 'lucide-react';

const QuestionnaireList = ({ ownerType, ownerId, onEdit, onAssign, onCreateNew, onShare, onCopy }) => {
  const [ownQuestionnaires, setOwnQuestionnaires] = useState([]);
  const [presetQuestionnaires, setPresetQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [copyingId, setCopyingId] = useState(null);

  useEffect(() => {
    loadQuestionnaires();
  }, [ownerType, ownerId]);

  const loadQuestionnaires = async () => {
    try {
      setLoading(true);
      let response;
      
      if (ownerType === 'admin') {
        response = await questionnaireAPI.getByAdmin(ownerId);
        setOwnQuestionnaires(response.data || []);
        setPresetQuestionnaires([]);
      } else if (ownerType === 'organization') {
        response = await questionnaireAPI.getByOrganization(ownerId);
        setOwnQuestionnaires(response.data.own || []);
        setPresetQuestionnaires(response.data.preset || []);
      } else if (ownerType === 'partner') {
        response = await questionnaireAPI.getByPartner(ownerId);
        setOwnQuestionnaires(response.data.own || []);
        setPresetQuestionnaires(response.data.preset || []);
      } else {
        // Fallback for backward compatibility
        response = await questionnaireAPI.getByPartner(ownerId);
        if (response.data.own) {
          setOwnQuestionnaires(response.data.own || []);
          setPresetQuestionnaires(response.data.preset || []);
        } else {
          setOwnQuestionnaires(response.data || []);
          setPresetQuestionnaires([]);
        }
      }
    } catch (err) {
      setError('Failed to load questionnaires');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (questionnaireId) => {
    try {
      setCopyingId(questionnaireId);
      await questionnaireAPI.copyQuestionnaire(questionnaireId);
      if (onCopy) {
        onCopy();
      }
      // Reload questionnaires after copying
      await loadQuestionnaires();
      alert('Questionnaire copied successfully!');
    } catch (err) {
      console.error('Failed to copy questionnaire:', err);
      alert(err.response?.data?.error || 'Failed to copy questionnaire');
    } finally {
      setCopyingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await questionnaireAPI.delete(id);
      setOwnQuestionnaires(ownQuestionnaires.filter(q => q.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete questionnaire');
      console.error(err);
    }
  };

  const renderQuestionnaireCard = (questionnaire, isPreset = false) => {
    const canEdit = !isPreset && onEdit;
    const canDelete = !isPreset;
    const canShare = !isPreset && onShare && (ownerType === 'admin' || ownerType === 'organization');
    const canCopy = isPreset && onCopy;

    return (
      <div
        key={questionnaire.id}
        className={`bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 border ${
          isPreset ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-dark-border'
        }`}
      >
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-text-primary">
              {questionnaire.name}
            </h3>
            {isPreset && (
              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                Preset
              </span>
            )}
          </div>
          {questionnaire.description && (
            <p className="text-sm text-gray-600 dark:text-dark-text-secondary line-clamp-2">
              {questionnaire.description}
            </p>
          )}
        </div>

        {/* Statistics */}
        <div className="mb-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-dark-text-secondary">Questions:</span>
            <span className="font-medium text-gray-800 dark:text-dark-text-primary">
              {questionnaire.question_count || 0}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-dark-text-secondary">Assigned to:</span>
            <span className="font-medium text-gray-800 dark:text-dark-text-primary">
              {questionnaire.assignment_count || 0} users
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-dark-text-secondary">Completed:</span>
            <span className="font-medium text-gray-800 dark:text-dark-text-primary">
              {questionnaire.completed_count || 0}
            </span>
          </div>
          <div className="mt-2">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600 dark:text-dark-text-secondary">Completion Rate:</span>
              <span className="font-medium text-gray-800 dark:text-dark-text-primary">
                {getCompletionRate(questionnaire)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-dark-bg-tertiary rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full transition-all"
                style={{ width: `${getCompletionRate(questionnaire)}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Created Date */}
        <div className="text-xs text-gray-500 dark:text-dark-text-tertiary mb-4">
          Created: {new Date(questionnaire.created_at).toLocaleDateString()}
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          {onAssign && (
            <button
              onClick={() => onAssign(questionnaire)}
              className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-full hover:bg-green-700 transition-colors"
            >
              Assign
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => onEdit(questionnaire.id)}
              className="flex-1 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-full hover:bg-primary-700 transition-colors"
            >
              Edit
            </button>
          )}
          {canShare && (
            <button
              onClick={() => onShare(questionnaire)}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-full hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
            >
              <Share2 className="h-4 w-4" />
              Share
            </button>
          )}
          {canCopy && (
            <button
              onClick={() => handleCopy(questionnaire.id)}
              disabled={copyingId === questionnaire.id}
              className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-full hover:bg-purple-700 transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
            >
              <Copy className="h-4 w-4" />
              {copyingId === questionnaire.id ? 'Copying...' : 'Copy'}
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setDeleteConfirm(questionnaire.id)}
              className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-full hover:bg-red-700 transition-colors"
            >
              Delete
            </button>
          )}
        </div>

        {/* Delete Confirmation */}
        {deleteConfirm === questionnaire.id && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
            <p className="text-sm text-red-800 dark:text-red-200 mb-2">
              Are you sure you want to delete this questionnaire?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleDelete(questionnaire.id)}
                className="flex-1 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
              >
                Yes, Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-3 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm rounded hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const filterQuestionnaires = (questionnaires) => {
    return questionnaires.filter(q =>
      q.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (q.description && q.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const filteredOwn = filterQuestionnaires(ownQuestionnaires);
  const filteredPreset = filterQuestionnaires(presetQuestionnaires);

  const getCompletionRate = (questionnaire) => {
    const total = parseInt(questionnaire.assignment_count) || 0;
    const completed = parseInt(questionnaire.completed_count) || 0;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-dark-text-primary">Questionnaires</h2>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 sm:px-6 sm:py-2 bg-primary-600 text-white text-sm sm:text-base rounded-lg hover:bg-primary-700 transition-colors"
        >
          + Create New Questionnaire
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-200 rounded">
          {error}
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search questionnaires..."
          className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary"
        />
      </div>

      {/* My Questionnaires Section */}
      {filteredOwn.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-text-primary mb-4">My Questionnaires</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredOwn.map((questionnaire) => renderQuestionnaireCard(questionnaire, false))}
          </div>
        </div>
      )}

      {/* Preset Questionnaires Section */}
      {filteredPreset.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-text-primary mb-4">Preset Questionnaires</h3>
          <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-4">
            These questionnaires are shared with you. You can copy them to create your own editable version.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPreset.map((questionnaire) => renderQuestionnaireCard(questionnaire, true))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredOwn.length === 0 && filteredPreset.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg border-2 border-dashed border-gray-300 dark:border-dark-border">
          <p className="text-gray-500 dark:text-dark-text-tertiary text-lg mb-4">
            {searchTerm ? 'No questionnaires found matching your search' : 'No questionnaires yet'}
          </p>
          {!searchTerm && onCreateNew && (
            <button
              onClick={onCreateNew}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Create Your First Questionnaire
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionnaireList;
